import { Alert, Box, Button, Typography } from '@mui/material'
import { Component, ErrorInfo, ReactNode } from 'react'

import { Sentry } from '@/config/sentry'

import { getUserFriendlyErrorMessage } from '../utils/errorMessages'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      error,
      errorInfo,
    })

    Sentry.withScope(scope => {
      scope.setExtras({ componentStack: errorInfo.componentStack })
      Sentry.captureException(error)
    })

    // Log error to console for debugging
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            height: '100%',
            padding: 3,
            backgroundColor: 'background.default',
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 600, width: '100%', mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" paragraph>
              {this.state.error
                ? getUserFriendlyErrorMessage(this.state.error.message)
                : 'An unexpected error occurred. Please try again.'}
            </Typography>
            {this.state.error && (
              <details
                open={
                  typeof window !== 'undefined' &&
                  ['localhost', '127.0.0.1'].includes(window.location.hostname)
                }
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    marginTop: '8px',
                    fontSize: '14px',
                  }}
                >
                  Error details
                </summary>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {this.state.error.message}
                  </Typography>
                  {this.state.errorInfo && (
                    <pre
                      style={{
                        fontSize: '11px',
                        marginTop: '8px',
                        whiteSpace: 'pre-wrap',
                        color: '#666',
                        maxHeight: '40vh',
                        overflow: 'auto',
                        border: '1px solid #eee',
                        borderRadius: '4px',
                        padding: '8px',
                      }}
                    >
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </Box>
              </details>
            )}
          </Alert>
          <Button variant="contained" onClick={this.handleRetry}>
            Try Again
          </Button>
        </Box>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
